import {
  IonContent,
  IonIcon,
  IonItem,
  IonItemDivider,
  IonLabel,
  IonList,
  IonListHeader,
  IonMenu,
  IonMenuToggle,
  IonToggle,
  IonModal,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonChip,
  IonProgressBar,
  IonButton,
  IonLoading,
  isPlatform,
} from "@ionic/react";
import {
  schoolSharp,
  cafeOutline,
  leafOutline,
  logIn,
  logOut,
  moonOutline,
  musicalNoteOutline,
  peopleOutline,
  person,
  radioOutline,
  starOutline,
  musicalNotesOutline,
  listOutline,
  folderOpenOutline,
  accessibilityOutline,
  checkmarkCircle,
  closeOutline,
  heartOutline,
  globeOutline,
} from "ionicons/icons";
import React, { useEffect, useRef, useState } from "react";
import { menuController } from "@ionic/core";
import { RouteComponentProps, useLocation, withRouter } from "react-router";
import { useProfile } from "../data/store/useProfile";
import HostConfig from "../HostConfig";
import { accessibilityService, AccessibilityReport } from "../services/AccessibilityService";
import { ENABLE_GROUPS, ENABLE_REGIONS, ENABLE_SUPPORT, FEED_AS_SONGS, IS_SADFAM } from "../config/env";
import { sadfamRecordsOutline } from "../assets/sadfamRecordsIcon";
import "./Menu.css";

export const LeftSidePages: Pages[] = [
  { title: "Songs", path: "/", icon: musicalNoteOutline, routerDirection: "root" },
  {
    title: "Artists",
    path: "/artists",
    icon: peopleOutline,
    routerDirection: "forward",
  },
  // {
  //   title: "",
  //   path: "/regions",
  //   icon: globeOutline,
  //   routerDirection: "forward",
  // },
];

const MissionPage: Pages = {
  title: "Mission",
  path: "/mission",
  icon: leafOutline,
  routerDirection: "back",
};
const ArtistsPage: Pages = {
  title: "Artists",
  path: "/artists",
  icon: peopleOutline,
  routerDirection: "forward",
};
const FeedPage: Pages = {
  // SON-31: labelled "Songs" on the Song Share instance, "Song Feed" elsewhere.
  title: FEED_AS_SONGS ? "Songs" : "Song Feed",
  path: "/feed",
  icon: musicalNotesOutline,
  routerDirection: "forward",
};

// Order unified across every instance to match SADFAM: Mission, then the
// feed, then Artists. FeedPage's own label still varies per instance
// (FEED_AS_SONGS: "Songs" vs the legacy "Song Feed") — only the position
// changed here, not the naming.
const SideMenuPages: Pages[] = [MissionPage, FeedPage, ArtistsPage];

const TopLoggedInPages: Pages[] = [
  {
    title: "Transmit",
    path: "/transmit",
    icon: radioOutline,
    routerDirection: "forward",
  },
  { title: "Account", path: "/account", icon: person, routerDirection: "forward" },
  {
    title: "Conversations",
    path: "/conversations",
    icon: cafeOutline,
    routerDirection: "forward",
  },

  { title: "Logout", path: "/logout", icon: logOut, routerDirection: "root" },
];

const LoggedInPages: Pages[] = [
  ...(ENABLE_GROUPS ? [{
    title: "Assignments",
    path: "/assignments",
    icon: schoolSharp,
    routerDirection: "forward" as const,
  }] : []),
  {
    title: "Transmit",
    path: "/transmit",
    icon: radioOutline,
    routerDirection: "forward",
  },
  {
    title: "Favorites",
    path: "/favorites",
    icon: starOutline,
    routerDirection: "root",
  },
  {
    title: "Playlists",
    path: "/playlists",
    icon: listOutline,
    routerDirection: "forward",
  },
  {
    title: "Albums",
    path: "/albums",
    icon: folderOpenOutline,
    routerDirection: "forward",
  },
  ...(ENABLE_REGIONS ? [{
    title: "Regions",
    path: "/regions",
    icon: globeOutline,
    routerDirection: "forward" as const,
  }] : []),
  {
    title: "Conversations",
    path: "/conversations?thread=start",
    icon: cafeOutline,
    routerDirection: "forward",
  },
  ...(ENABLE_SUPPORT ? [{
    title: "Support",
    path: "/support",
    icon: heartOutline,
    routerDirection: "forward" as const,
  }] : []),
  ...(IS_SADFAM ? [{
    title: "SADFAM Records",
    href: "https://sadfamrecords.bandcamp.com/",
    icon: sadfamRecordsOutline,
  }] : []),
  {
    title: "Account",
    path: "/account",
    icon: person,
    routerDirection: "forward",
  },
  // { title: 'Support', path: '/support', icon: help },
  // { title: 'Notices', path: '/notices', icon: alertOutline },
  { title: "Logout", path: "/logout", icon: logOut, routerDirection: "root" },
];

const DesktopOnlyPages: Pages[] = [
  // { title: 'Visualizer', path: '/visualizer', icon: eyeOutline, routerDirection: "forward" }
];

const LoggedOutPages: Pages[] = [
  { title: "Login", path: "/login", icon: logIn, routerDirection: "forward" },
];

export const AllRoutes = {
  appPages: SideMenuPages,
  topMenuLoggedInPages: TopLoggedInPages,
  topLeftPages: LeftSidePages,
  loggedInPages: LoggedInPages,
  loggedOutPages: LoggedOutPages,
  desktopOnlyPages: DesktopOnlyPages,
};

export interface Pages {
  title: string;
  path?: string;
  href?: string;
  icon: string;
  routerDirection?: "back" | "forward" | "root" | undefined;
}

interface MenuProps extends RouteComponentProps {}

const Menu: React.FC<MenuProps> = () => {
  const { darkMode, authenticated, setDarkMode, menuEnabled } = useProfile();
  const location = useLocation();
  const [scanning, setScanning] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [report, setReport] = useState<AccessibilityReport | null>(null);
  const menuRef = useRef<HTMLIonMenuElement>(null);

  // The Groups / primary side menu is never opened by swipe — always require
  // the hamburger button. Swipe-to-open from any edge was being accidentally
  // triggered by drags elsewhere in the UI (e.g. the volume slider, or
  // pointer gestures near the right edge), pulling Groups in unexpectedly.
  // Ionic v5's <ion-menu swipeGesture> prop is not reliably reactive after
  // mount, so we also imperatively set the property on the underlying web
  // component AND go through menuController for triple-redundancy.
  useEffect(() => {
    if (menuRef.current) {
      (menuRef.current as any).swipeGesture = false;
    }
    menuController.swipeGesture(false, "primary").catch(() => { /* ignore */ });
  }, []);

  const runAccessibilityScan = async () => {
    setScanning(true);
    try {
      const pageName = location.pathname === '/' ? 'Home' : location.pathname.replace(/\//g, ' ').trim();
      const result = await accessibilityService.scanCurrentPage(pageName);
      const generatedReport = accessibilityService.generateReport([result]);
      setReport(generatedReport);
      setShowReport(true);
    } catch (error) {
      console.error('Accessibility scan failed:', error);
    } finally {
      setScanning(false);
    }
  };

  function renderListItems(list: Pages[]) {
    return list
      .filter((route) => !!route.path || !!route.href)
      .map((p) => (
        <IonMenuToggle key={p.title} auto-hide="false">
          <IonItem
            detail={false}
            {...(p.href
              ? { href: p.href, target: "_blank", rel: "noopener noreferrer" }
              : { routerLink: p.path, routerDirection: "root" as const }
            )}
            className={location.pathname === p.path ? "selected" : undefined}
          >
            <IonIcon slot="start" icon={p.icon} aria-hidden="true" />
            <IonLabel>{p.title}</IonLabel>
          </IonItem>
        </IonMenuToggle>
      ));
  }

  return (
    <>
      <IonMenu
        ref={menuRef}
        menuId="primary"
        side="start"
        type="overlay"
        disabled={!menuEnabled}
        swipeGesture={false}
        contentId="main"
      >
        <IonContent forceOverscroll={false}>
          <nav aria-label="Main navigation">
            <IonList lines="none">
              <IonListHeader>{HostConfig.title}</IonListHeader>
              {renderListItems(AllRoutes.appPages)}
            </IonList>
            <IonItemDivider/>
            <IonList lines="none">
              {!isPlatform("ios") && renderListItems(AllRoutes.desktopOnlyPages)}
              {authenticated
                ? renderListItems(AllRoutes.loggedInPages)
                : renderListItems(AllRoutes.loggedOutPages)}
              <IonItem>
                <IonIcon slot="start" icon={moonOutline} aria-hidden="true" />
                <IonLabel>Dark Mode</IonLabel>
                <IonToggle
                  checked={darkMode}
                  onClick={() => setDarkMode(!darkMode)}
                  aria-label="Toggle dark mode"
                />
              </IonItem>
            </IonList>
          </nav>
        </IonContent>
      </IonMenu>

      <IonLoading isOpen={scanning} message="Scanning page for accessibility issues..." />

      <IonModal isOpen={showReport} onDidDismiss={() => setShowReport(false)}>
        {report && (
          <IonContent>
            <IonCard>
              <IonCardHeader>
                <IonCardTitle style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>Accessibility Report</span>
                  <IonButton fill="clear" onClick={() => setShowReport(false)} aria-label="Close">
                    <IonIcon icon={closeOutline} aria-hidden="true" />
                  </IonButton>
                </IonCardTitle>
              </IonCardHeader>
              <IonCardContent>
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <IonIcon
                    icon={checkmarkCircle}
                    style={{
                      fontSize: '48px',
                      color: accessibilityService.getScoreColor(report.overallScore),
                    }}
                    aria-hidden="true"
                  />
                  <h2
                    style={{
                      fontSize: '36px',
                      margin: '10px 0',
                      color: accessibilityService.getScoreColor(report.overallScore),
                    }}
                  >
                    {report.overallScore}%
                  </h2>
                  <IonProgressBar
                    value={report.overallScore / 100}
                    color={report.overallScore >= 90 ? 'success' : report.overallScore >= 70 ? 'warning' : 'danger'}
                  />
                </div>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'center', margin: '16px 0' }}>
                  <IonChip color="danger">Critical: {report.criticalCount}</IonChip>
                  <IonChip color="warning">Serious: {report.seriousCount}</IonChip>
                  <IonChip color="secondary">Moderate: {report.moderateCount}</IonChip>
                  <IonChip color="medium">Minor: {report.minorCount}</IonChip>
                </div>

                {report.pageResults[0]?.violations.length === 0 ? (
                  <p style={{ textAlign: 'center', color: 'var(--ion-color-success)' }}>
                    No accessibility violations found on this page!
                  </p>
                ) : (
                  <IonList>
                    {report.pageResults[0]?.violations.map((violation, index) => (
                      <IonItem key={index}>
                        <IonChip
                          slot="start"
                          color={
                            violation.impact === 'critical' ? 'danger' :
                            violation.impact === 'serious' ? 'warning' :
                            violation.impact === 'moderate' ? 'secondary' : 'medium'
                          }
                        >
                          {violation.impact}
                        </IonChip>
                        <IonLabel className="ion-text-wrap">
                          <h3>{violation.id}</h3>
                          <p>{violation.help}</p>
                          <p style={{ fontSize: '12px', color: 'var(--ion-color-medium)' }}>
                            {violation.nodes.length} element{violation.nodes.length !== 1 ? 's' : ''} affected
                          </p>
                        </IonLabel>
                      </IonItem>
                    ))}
                  </IonList>
                )}

                <div style={{ textAlign: 'center', marginTop: '16px' }}>
                  <IonButton expand="block" onClick={() => setShowReport(false)}>
                    Close
                  </IonButton>
                </div>
              </IonCardContent>
            </IonCard>
          </IonContent>
        )}
      </IonModal>
    </>
  );
};

export default withRouter(Menu);
